// B2B / Nabídka a ceník (schválený mockup 8): produkty s cenami a text "Co potřebujeme od vás". Upravuje jen owner.
import React, { useCallback, useEffect, useState } from "react";
import { Tag, Plus, Pencil, FileDown, Clock, Users } from "lucide-react";
import GradientHeader from "../../components/GradientHeader.jsx";
import Card from "../../components/Card.jsx";
import { Stranka, Modal, HlavickaOkna, Field, Hlaska, RozbalovaciVyber, Zapinatko, ErrorNote, SkeletonRows } from "../../components/ui.jsx";
import { Pil } from "../../components/sales/salesUi.jsx";
import { useUzkaObrazovka } from "../../lib/obrazovka.js";
import { toast } from "../../components/Toast.jsx";
import { b2bGet, b2bPost } from "../../components/b2b/b2bApi.js";
import { cenaText, JEDNOTKY, volbySegmentu, SEGMENTY } from "../../components/b2b/b2bUi.jsx";

const dobaText = (m) => (m >= 60 ? `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60} min` : ""}` : `${m} min`);

function Produkt({ p, muze, onUpravit }) {
  return (
    <div className={`polozka space-y-2 ${p.aktivni ? "" : "opacity-70"}`}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-base font-bold leading-snug text-slate-900">{p.nazev}</div>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            <Pil ton={p.cena_od == null && p.cena_do == null ? "pozor" : "znacka"} casti={[cenaText(p), p.cena_od == null && p.cena_do == null ? null : JEDNOTKY[p.jednotka]]} />
            {p.segment && <Pil ton="neutralni" casti={SEGMENTY[p.segment]?.label || p.segment} />}
            {p.delka_min > 0 && <Pil ton="neutralni" ikona={Clock} casti={dobaText(p.delka_min)} />}
            {p.kapacita_text && <Pil ton="neutralni" ikona={Users} casti={p.kapacita_text} />}
            {!p.aktivni && <Pil ton="neutralni" casti="neaktivní" />}
          </div>
        </div>
        {muze && <button type="button" className="btn-ikona shrink-0" aria-label={`Upravit produkt ${p.nazev}`} title="Upravit" onClick={onUpravit}><Pencil className="h-4 w-4" /></button>}
      </div>
      {p.popis && <p className="text-[14px] leading-snug text-slate-700">{p.popis}</p>}
    </div>
  );
}

const PRAZDNY = { nazev: "", popis: "", cena_od: "", cena_do: "", jednotka: "akce", segment: "firma", delka_min: "", kapacita_text: "", aktivni: true };

function OknoProduktu({ p, onClose, onHotovo }) {
  const [f, setF] = useState(PRAZDNY);
  const [busy, setBusy] = useState(false);
  const [chyba, setChyba] = useState("");
  useEffect(() => {
    setChyba("");
    setF(p?.id ? { nazev: p.nazev, popis: p.popis || "", cena_od: p.cena_od ?? "", cena_do: p.cena_do ?? "", jednotka: p.jednotka, segment: p.segment || "", delka_min: p.delka_min ?? "", kapacita_text: p.kapacita_text || "", aktivni: p.aktivni } : PRAZDNY);
  }, [p]);
  if (!p) return null;
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const uloz = async () => {
    setBusy(true); setChyba("");
    try {
      if (p.id) await b2bPost("produkt_upravit", { id: p.id, ...f }); else await b2bPost("produkt_pridat", f);
      toast("Produkt uložen.", "success");
      onHotovo();
    } catch (e) { setChyba(e.message); }
    finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} size="lg" plnaHlavicka
      header={<HlavickaOkna ikona={Tag} nazev={p.id ? "Upravit produkt" : "Přidat produkt"} popis={p.id ? p.nazev : undefined} onClose={onClose} />}
      footer={<><button type="button" className="btn-secondary" onClick={onClose}>Zrušit</button><button type="button" className="btn-primary" onClick={uloz} disabled={busy || !f.nazev.trim()} aria-busy={busy}>Uložit</button></>}>
      <div className="space-y-4">
        {chyba && <Hlaska ton="problem">{chyba}</Hlaska>}
        <Field label="Název"><input className="field" value={f.nazev} onChange={set("nazev")} /></Field>
        <Field label="Popis (jde do nabídky PDF)"><textarea className="field" rows={3} value={f.popis} onChange={set("popis")} /></Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Cena od, Kč bez DPH"><input className="field" inputMode="decimal" value={f.cena_od} onChange={set("cena_od")} /></Field>
          <Field label="Cena do, Kč bez DPH"><input className="field" inputMode="decimal" value={f.cena_do} onChange={set("cena_do")} /></Field>
          <RozbalovaciVyber stitek="Jednotka" aria-label="Jednotka" value={f.jednotka} onChange={(v) => setF((x) => ({ ...x, jednotka: v }))} items={Object.entries(JEDNOTKY).map(([k, l]) => ({ key: k, label: l.replace("za ", "Za ") }))} />
          <RozbalovaciVyber stitek="Segment" aria-label="Segment" value={f.segment} onChange={(v) => setF((x) => ({ ...x, segment: v }))} items={volbySegmentu("Bez segmentu")} />
          <Field label="Délka, minuty"><input className="field" inputMode="numeric" value={f.delka_min} onChange={set("delka_min")} /></Field>
          <Field label="Kapacita"><input className="field" value={f.kapacita_text} onChange={set("kapacita_text")} placeholder="například 30 až 60 lidí" /></Field>
        </div>
        <Zapinatko zapnuto={f.aktivni} onZmena={(v) => setF((x) => ({ ...x, aktivni: v }))} popisek="Produkt je v nabídce" />
      </div>
    </Modal>
  );
}

export default function Cenik() {
  const uzka = useUzkaObrazovka();
  const [d, setD] = useState(null);
  const [chyba, setChyba] = useState("");
  const [edit, setEdit] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const nacti = useCallback(async () => {
    try { const r = await b2bGet("produkty"); setD(r); setText(r.nabidka?.co_potrebujeme || ""); setChyba(""); }
    catch (e) { setChyba(e.message); }
  }, []);
  useEffect(() => { nacti(); }, [nacti]);

  if (chyba) return <Stranka sirka="plna"><ErrorNote onRetry={nacti}>{chyba}</ErrorNote></Stranka>;
  if (!d) return <Stranka sirka="plna"><SkeletonRows rows={5} /></Stranka>;
  const muze = d.muze_upravit;
  const ulozText = async () => {
    setBusy(true);
    try { await b2bPost("nabidka_ulozit", { co_potrebujeme: text }); toast("Text nabídky uložen.", "success"); nacti(); }
    catch (e) { toast(e.message, "error"); }
    finally { setBusy(false); }
  };
  const chybiCeny = d.produkty.some((p) => p.aktivni && p.cena_od == null && p.cena_do == null);

  const produkty = (
    <Card title="Produkty a ceny" ikona={Tag} odznak={d.produkty.length}
      action={muze ? <button type="button" className="btn-secondary btn-sm" onClick={() => setEdit({})}><Plus className="h-4 w-4" />Přidat produkt</button> : null}>
      <div className="space-y-3">{d.produkty.map((p) => <Produkt key={p.id} p={p} muze={muze} onUpravit={() => setEdit(p)} />)}</div>
    </Card>
  );
  const nabidka = (
    <Card title="Nabídka PDF" ikona={FileDown}>
      <div className="space-y-3">
        <p className="text-sm text-slate-700">PDF se skládá z hlavičky FitWay40, firmy, produktu, harmonogramu, ceny a odstavce Co potřebujeme od vás. Generuje se z detailu akce.</p>
        <Field label="Co potřebujeme od vás (jde do každé nabídky)">
          <textarea className="field" rows={5} value={text} onChange={(e) => setText(e.target.value)} disabled={!muze} />
        </Field>
        {muze && <button type="button" className="btn-primary" onClick={ulozText} disabled={busy || text === (d.nabidka?.co_potrebujeme || "")} aria-busy={busy}>Uložit</button>}
      </div>
    </Card>
  );
  return (
    <Stranka sirka="plna">
      <GradientHeader title="Nabídka a ceník" icon={Tag} />
      {muze
        ? (chybiCeny && <Hlaska ton="pozor" title="Ceny zatím nejsou vyplněné">U produktů označených "cena zatím není" doplň cenu tužkou. Nabídka se z cen plní sama.</Hlaska>)
        : <p className="text-sm text-slate-600">Ceník upravuje jen owner. Tady je ke čtení.</p>}
      {uzka ? <div className="space-y-4">{produkty}{nabidka}</div> : <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_420px]">{produkty}{nabidka}</div>}
      <OknoProduktu p={edit} onClose={() => setEdit(null)} onHotovo={() => { setEdit(null); nacti(); }} />
    </Stranka>
  );
}
